import { documentSchema } from "@prismicio/editor-fields"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  Document,
  isSharedSliceContent,
  SharedSliceContent,
} from "@prismicio/types-internal/lib/content"
import type {
  SharedSlice,
  Variation,
} from "@prismicio/types-internal/lib/customtypes"
import { z } from "zod"

import type { SliceConfigWithSharedSlice } from "../../document/LeftSidePanel/sliceTable/sliceTableData"
import { env } from "../env"

type MessageCallback<Message> = (message: Message) => void

interface CreateSlicesArgs {
  uuid: string
  slices: SliceConfigWithSharedSlice[]
  document?: Document
  prompt: string
  locale?: string
  onMessage: MessageCallback<CreateSlicesMessage>
}

export function createSlices(args: CreateSlicesArgs) {
  const {
    uuid,
    slices: sliceConfigs,
    document,
    prompt,
    locale,
    onMessage,
  } = args

  const slices = sliceConfigs.map(({ sliceConfig }) => sliceConfig)
  const schema = getCreateSlicesSchema({ slices })

  socketRequest({
    uuid,
    onOpen: () =>
      request(new URL("create-slices", env.PRISMIC_AI_SERVICE_BASE_URL), {
        credentials: getRequestCredentials(
          env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        ),
        method: "POST",
        body: { uuid, slices, document, prompt, locale },
        okType: z.unknown(),
      }),
    schema,
    onMessage: (message) => {
      onMessage(message)
      switch (message.status) {
        case "filling":
        case "slicing":
          return { done: false }
        case "done":
        case "failed":
          return { done: true }
      }
    },
    onError: () => onMessage({ status: "failed" }),
  })
}

function getCreateSlicesSchema({ slices }: { slices: SharedSlice[] }) {
  const [firstSliceId, ...restSliceIds] = slices.map(({ id }) => id)

  if (firstSliceId === undefined)
    throw new Error("At least one slice is required.")

  const sliceIdSchema = z.enum([firstSliceId, ...restSliceIds])

  return z.discriminatedUnion("status", [
    z.object({ status: z.literal("slicing") }),
    z.object({
      status: z.literal("filling"),
      slice: sliceIdSchema,
    }),
    z.object({
      status: z.literal("done"),
      slice: sliceIdSchema,
      slices: z
        .array(z.custom<SharedSliceContent>(isSharedSliceContent))
        .nonempty(),
      cost: z.number(),
    }),
    z.object({ status: z.literal("failed") }),
  ])
}

type CreateSlicesMessage = z.infer<ReturnType<typeof getCreateSlicesSchema>>

interface RefineSliceArgs {
  uuid: string
  document?: Document
  variation: Variation
  content: SharedSliceContent
  prompt: string
  locale?: string
  onMessage: MessageCallback<RefineSliceMessage>
}

export function refineSlice(args: RefineSliceArgs) {
  const { uuid, document, variation, content, prompt, locale, onMessage } = args

  socketRequest({
    uuid,
    onOpen: () =>
      request(new URL("refine-slice", env.PRISMIC_AI_SERVICE_BASE_URL), {
        credentials: getRequestCredentials(
          env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        ),
        method: "POST",
        body: { uuid, document, variation, content, prompt, locale },
        okType: z.unknown(),
      }),
    schema: refineSliceMessageSchema,
    onMessage: (message) => {
      onMessage(message)
      switch (message.status) {
        case "refining":
          return { done: false }
        case "done":
        case "failed":
          return { done: true }
      }
    },
    onError: () => onMessage({ status: "failed" }),
  })
}

const refineSliceMessageSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("refining") }),
  z.object({
    status: z.literal("done"),
    slice: z.custom<SharedSliceContent>(isSharedSliceContent),
    cost: z.number(),
  }),
  z.object({ status: z.literal("failed") }),
])

type RefineSliceMessage = z.infer<typeof refineSliceMessageSchema>

interface FitHtmlIntoSliceArgs {
  uuid: string
  slices: SharedSlice[]
  slice: string
  variation: string
  html: string
  onMessage: MessageCallback<FitHtmlIntoSliceMessage>
}

export function fitHtmlIntoSlice(args: FitHtmlIntoSliceArgs) {
  const { uuid, slices, slice, variation, html, onMessage } = args

  socketRequest({
    uuid,
    onOpen: () =>
      request(new URL("fit-slice", env.PRISMIC_AI_SERVICE_BASE_URL), {
        credentials: getRequestCredentials(
          env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        ),
        method: "POST",
        body: { uuid, slices, slice, variation, html },
        okType: z.unknown(),
      }),
    schema: fitHtmlIntoSliceMessageSchema,
    onMessage: (message) => {
      onMessage(message)
      switch (message.status) {
        case "fitting":
          return { done: false }
        case "done":
        case "failed":
          return { done: true }
      }
    },
    onError: () => onMessage({ status: "failed" }),
  })
}

const fitHtmlIntoSliceMessageSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("fitting") }),
  z.object({
    status: z.literal("done"),
    slice: z.custom<SharedSliceContent>(isSharedSliceContent),
    cost: z.number(),
    distance: z.number(),
  }),
  z.object({ status: z.literal("failed") }),
])

type FitHtmlIntoSliceMessage = z.infer<typeof fitHtmlIntoSliceMessageSchema>

interface TranslateDocumentArgs {
  uuid: string
  document: Document
  locale: string
  onMessage: MessageCallback<TranslateDocumentMessage>
}

export function translateDocument(args: TranslateDocumentArgs) {
  const { uuid, document, locale, onMessage } = args

  socketRequest({
    uuid,
    onOpen: () =>
      request(new URL("translate-document", env.PRISMIC_AI_SERVICE_BASE_URL), {
        credentials: getRequestCredentials(
          env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        ),
        method: "POST",
        body: { uuid, document, locale },
        okType: z.unknown(),
      }),
    schema: translateDocumentMessageSchema,
    onMessage: (message) => {
      onMessage(message)
      switch (message.status) {
        case "translating":
          return { done: false }
        case "done":
        case "failed":
          return { done: true }
      }
    },
    onError: () => onMessage({ status: "failed" }),
  })
}

const translateDocumentMessageSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("translating") }),
  z.object({
    status: z.literal("done"),
    document: documentSchema,
    model: z.string(),
    cost: z.number(),
    tokensIn: z.number(),
    tokensOut: z.number(),
  }),
  z.object({ status: z.literal("failed") }),
])

type TranslateDocumentMessage = z.infer<typeof translateDocumentMessageSchema>

interface SocketRequestArgs<Message> {
  uuid: string
  onOpen: () => Promise<unknown>
  schema: z.ZodType<Message, z.ZodTypeDef, unknown>
  /**
   * @param message
   * @returns A boolean indicating whether the operation is done.
   */
  onMessage: (message: Message) => { done: boolean }
  onError: (error: unknown) => void
}

function socketRequest<Message>(args: SocketRequestArgs<Message>) {
  const { uuid, onOpen, schema, onMessage, onError } = args

  const url = new URL(env.PRISMIC_AI_SERVICE_WEBSOCKET_ENDPOINT)
  url.searchParams.set("uuid", uuid)
  const websocket = new WebSocket(url)

  websocket.onopen = () =>
    onOpen().catch((error) => {
      onError(error)
      cleanup()
    })

  websocket.onmessage = (event: MessageEvent<string>) => {
    const message = schema.safeParse(JSON.parse(event.data))

    let done
    if (message.success) {
      done = onMessage(message.data).done
    } else {
      done = true
      onError(message.error)
    }

    if (done) cleanup()
  }

  websocket.onerror = () => {
    onError(new Error("The socket connection got interrupted."))
    cleanup()
  }

  const timeout = setTimeout(
    () => {
      onError(new Error("The socket connection timed out."))
      cleanup()
    },
    5 * 60 * 1000,
  ) // 5 minutes.

  function cleanup() {
    clearTimeout(timeout)
    websocket.onopen = null
    websocket.onmessage = null
    websocket.onerror = null
    websocket.close()
  }
}
